import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Calendar, MapPin, Users, Clock, ChevronRight, Heart, Award } from "lucide-react";
import axios from "axios";
import SoldOutBanner from "../components/common/SoldOutBanner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function HomePage() {
  const [pricing, setPricing] = useState(null);
  const [additionalSponsors, setAdditionalSponsors] = useState([]);

  // SCAO is always shown as the primary beneficiary sponsor
  const defaultSponsors = [
    {
      id: "scao-default",
      url: "https://customer-assets.emergentagent.com/job_9d54988f-f3b8-4fd6-bed5-62f15182622d/artifacts/g0wk6ky2_SCAO-logo-stacked-min.png",
      name: "Sickle Cell Association of Ontario"
    }
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [pricingRes, sponsorsRes] = await Promise.all([
          axios.get(`${API}/pricing`),
          axios.get(`${API}/sponsor-logos`)
        ]);
        setPricing(pricingRes.data);
        setAdditionalSponsors(sponsorsRes.data);
      } catch (err) {
        console.error("Failed to fetch data:", err);
      }
    };
    fetchData();
  }, []);

  // Combine default sponsors with any additional ones from the database
  // Filter out duplicates (in case SCAO is also added via admin)
  const allSponsors = [
    ...defaultSponsors,
    ...additionalSponsors.filter(s => !s.url.includes('SCAO-logo-stacked-min'))
  ];

  return (
    <div className="min-h-screen">
      {/* Sold Out Banner */}
      <SoldOutBanner />

      {/* Hero Section */}
      <section className="relative min-h-[85vh] md:min-h-[80vh] flex items-center">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src="https://images.pexels.com/photos/31803613/pexels-photo-31803613.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
            alt="Royal Woodbine Golf Club"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1a3020]/95 via-[#2A4430]/85 to-[#2A4430]/60" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 w-full py-16 md:py-24">
          <div className="max-w-3xl">
            {/* Date Badge */}
            <div 
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/15 backdrop-blur-sm border border-white/25 text-white text-sm mb-6 animate-fade-in-up"
              data-testid="event-date-badge"
            >
              <Calendar size={16} />
              <span className="font-medium">August 11, 2026</span>
            </div>

            {/* Headline */}
            <h1 
              className="text-5xl md:text-7xl font-light tracking-tight leading-[0.95] text-white mb-6 animate-fade-in-up animation-delay-100"
              data-testid="hero-headline"
            >
              BISN<br />
              <span className="font-medium">Charity Golf Classic</span>
            </h1>

            {/* Tagline */}
            <p 
              className="text-xl md:text-2xl text-white font-light leading-relaxed mb-2 animate-fade-in-up animation-delay-200"
              data-testid="hero-tagline"
            >
              A beautiful day of golf.
            </p>
            <p className="text-2xl md:text-3xl mb-6 animate-fade-in-up animation-delay-200" style={{ fontFamily: '"Italianno", cursive', color: '#C5A059' }}>
              A powerful day for community.
            </p>

            {/* Supporting Text */}
            <p className="text-base text-white/80 max-w-md mb-8 animate-fade-in-up animation-delay-300">
              Supporting the Sickle Cell Association of Ontario
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up animation-delay-400">
              <Link
                to="/register"
                className="inline-flex items-center justify-center gap-2 bg-white text-[#2A4430] hover:bg-[#F3F0E6] rounded-full px-8 py-4 text-lg font-medium transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-1"
                data-testid="hero-register-btn"
              >
                Register Now
                <ChevronRight size={20} />
              </Link>
              <Link
                to="/sponsorship"
                className="inline-flex items-center justify-center gap-2 bg-transparent text-white border-2 border-white/50 hover:bg-white/10 rounded-full px-8 py-4 text-lg font-medium transition-all duration-300"
                data-testid="hero-sponsor-btn"
              >
                Become a Sponsor
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Signals */}
      <section className="py-8 bg-[#F9F8F4]">
        <div className="container-main">
          <div 
            className="flex flex-wrap items-center justify-center gap-6 md:gap-12 text-sm text-[#666055]"
            data-testid="trust-signals"
          >
            <div className="flex items-center gap-2">
              <Users size={18} className="text-[#3A5F40]" />
              <span>144 Golfers</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock size={18} className="text-[#3A5F40]" />
              <span>8:00 AM Shotgun Start</span>
            </div>
            <div className="flex items-center gap-2">
              <MapPin size={18} className="text-[#3A5F40]" />
              <span>Royal Woodbine Golf Club</span>
            </div>
            <div className="flex items-center gap-2">
              <Heart size={18} className="text-[#C5A059]" />
              <span>Sickle Cell Association of Ontario</span>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Preview */}
      <section className="section-padding bg-[#F9F8F4]">
        <div className="container-main">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <p className="text-xs font-bold uppercase tracking-widest text-[#666055] mb-3">
                Registration
              </p>
              <h2 className="text-4xl md:text-5xl tracking-tight text-[#1C1C1C]">
                Registration is Now Closed.
                <br />
                <span className="text-[#3A5F40]">See You on the Green!</span>
              </h2>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {/* Early Bird Card */}
              <div 
                className={`bg-white rounded-2xl border border-[#E6E2D6] p-8 shadow-[0_4px_20px_rgba(0,0,0,0.03)] transition-all duration-300 hover:shadow-[0_12px_40px_rgba(0,0,0,0.06)] ring-2 ring-[#C5A059]`}
                data-testid="early-bird-card"
              >
                <span className="inline-block px-3 py-1 rounded-full bg-[#C5A059] text-white text-xs font-medium mb-4">
                  Current Rate
                </span>
                <h3 className="text-2xl text-[#1C1C1C] mb-2">Early Bird</h3>
                <p className="text-sm text-[#C5A059] font-medium mb-4">Extended for a limited time only!</p>
                <p className="text-4xl font-medium text-[#3A5F40] mb-1">
                  $180 <span className="text-lg font-normal text-[#666055]">/ golfer</span>
                </p>
                <p className="text-sm text-[#666055] mt-2">Foursome: $720</p>
              </div>

              {/* Regular Card */}
              <div 
                className={`bg-white rounded-2xl border border-[#E6E2D6] p-8 shadow-[0_4px_20px_rgba(0,0,0,0.03)] transition-all duration-300 hover:shadow-[0_12px_40px_rgba(0,0,0,0.06)]`}
                data-testid="regular-card"
              >
                <h3 className="text-2xl text-[#1C1C1C] mb-2">Regular</h3>
                <p className="text-sm text-[#666055] mb-4">Standard pricing</p>
                <p className="text-4xl font-medium text-[#3A5F40] mb-1">
                  $215 <span className="text-lg font-normal text-[#666055]">/ golfer</span>
                </p>
                <p className="text-sm text-[#666055] mt-2">Foursome: $860</p>
              </div>
            </div>

            <div className="text-center mt-8">
              <Link
                to="/register"
                className="btn-primary inline-flex items-center gap-2"
                data-testid="pricing-register-btn"
              >
                Register Your Spot
                <ChevronRight size={20} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Why Support Section */}
      <section className="section-padding bg-white">
        <div className="container-main">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-xs font-bold uppercase tracking-widest text-[#666055] mb-3">
              Our Impact
            </p>
            <h2 className="text-4xl md:text-5xl tracking-tight text-[#1C1C1C] mb-8">
              More Than a Game
            </h2>
            <p className="text-lg text-[#666055] leading-relaxed mb-8">
              Sickle Cell Disease affects thousands of Canadians, particularly within our Black community. 
              Your participation helps fund vital research, patient support programs, and raises awareness 
              for a condition that often goes overlooked. Together, we're building a healthier future.
            </p>
            <div className="gold-line mx-auto mb-8" />
            <Link
              to="/info"
              className="text-[#3A5F40] font-medium hover:text-[#2A4430] transition-colors inline-flex items-center gap-1"
              data-testid="learn-more-link"
            >
              Learn more about the cause
              <ChevronRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* Our Sponsors Section - Always shows with SCAO + any additional sponsors */}
      <section className="section-padding bg-[#F9F8F4]">
        <div className="container-main">
          <div className="text-center mb-12">
            <p className="text-xs font-bold uppercase tracking-widest text-[#666055] mb-3">
              Thank You To
            </p>
            <h2 className="text-4xl md:text-5xl tracking-tight text-[#1C1C1C]">
              Our Sponsors
            </h2>
          </div>
          
          <div className="max-w-5xl mx-auto">
            <div 
              className={`grid gap-8 items-center justify-items-center ${
                allSponsors.length === 1 ? 'grid-cols-1' :
                allSponsors.length === 2 ? 'grid-cols-2' :
                allSponsors.length === 3 ? 'grid-cols-3' :
                'grid-cols-2 md:grid-cols-3 lg:grid-cols-4'
              }`}
              data-testid="sponsors-grid"
            >
              {allSponsors.map((logo, index) => (
                <div 
                  key={logo.id || index}
                  className="bg-white rounded-2xl border border-[#E6E2D6] p-6 flex items-center justify-center aspect-[3/2] w-full max-w-[250px] transition-all duration-300 hover:shadow-lg"
                  data-testid={`sponsor-logo-${index}`}
                >
                  <img
                    src={logo.url}
                    alt={logo.name || "Sponsor"}
                    className="max-w-full max-h-full object-contain"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="text-center mt-12">
            <Link
              to="/sponsorship"
              className="text-[#C5A059] font-medium hover:text-[#B8934D] transition-colors inline-flex items-center gap-1"
              data-testid="become-sponsor-link"
            >
              Become a Founding Sponsor
              <ChevronRight size={18} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
